import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    LogOut,
    Mail,
    Phone,
    MessageSquare,
    Calendar,
    Search,
    Trash2,
    Check,
    Download,
    ChevronDown,
    Loader2,
    Inbox,
} from "lucide-react";
import { api, clearToken, getToken } from "../lib/adminApi";
import BrandLogo from "../components/BrandLogo";

export default function AdminLeads() {
    const navigate = useNavigate();
    const [leads, setLeads] = useState(null);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("all"); // all | unread | read
    const [opened, setOpened] = useState(new Set());
    const [actionBusy, setActionBusy] = useState("");

    useEffect(() => {
        if (!getToken()) {
            navigate("/admin/login", { replace: true });
            return;
        }
        load();
    }, []); // eslint-disable-line

    const load = async () => {
        try {
            const { data } = await api.get("/admin/leads");
            setLeads(data);
        } catch (e) {
            setLeads([]);
        }
    };

    const filtered = useMemo(() => {
        if (!leads) return [];
        const q = query.trim().toLowerCase();
        return leads.filter((l) => {
            if (filter === "unread" && l.read) return false;
            if (filter === "read" && !l.read) return false;
            if (!q) return true;
            return (
                (l.name || "").toLowerCase().includes(q) ||
                (l.email || "").toLowerCase().includes(q) ||
                (l.experience || "").toLowerCase().includes(q) ||
                (l.phone || "").toLowerCase().includes(q) ||
                (l.message || "").toLowerCase().includes(q)
            );
        });
    }, [leads, query, filter]);

    const unreadCount = useMemo(
        () => (leads || []).filter((l) => !l.read).length,
        [leads],
    );

    const toggleRead = async (lead) => {
        setActionBusy(lead.id);
        try {
            const next = !lead.read;
            await api.patch(`/admin/leads/${lead.id}/read`, { read: next });
            setLeads((prev) => prev.map((l) => (l.id === lead.id ? { ...l, read: next } : l)));
        } finally {
            setActionBusy("");
        }
    };

    const remove = async (lead) => {
        if (!window.confirm(`Supprimer la demande de ${lead.name} ?`)) return;
        setActionBusy(lead.id);
        try {
            await api.delete(`/admin/leads/${lead.id}`);
            setLeads((prev) => prev.filter((l) => l.id !== lead.id));
        } finally {
            setActionBusy("");
        }
    };

    const exportCsv = () => {
        const rows = [
            ["Date", "Nom", "Email", "Téléphone", "Expérience", "Offre", "Add-ons", "Message", "Source", "Lu"],
            ...(leads || []).map((l) => [
                new Date(l.created_at).toLocaleString("fr-FR"),
                l.name,
                l.email,
                l.phone || "",
                l.experience || "",
                l.offer || "",
                (l.addons || []).join("; "),
                (l.message || "").replace(/\n/g, " "),
                l.source || "",
                l.read ? "oui" : "non",
            ]),
        ];
        const csv = rows
            .map((r) =>
                r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","),
            )
            .join("\n");
        const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `exotik-trips-leads-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const logout = () => {
        clearToken();
        navigate("/admin/login", { replace: true });
    };

    const toggleOpen = (id) => {
        setOpened((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    return (
        <div className="min-h-screen bg-cream">
            {/* Top bar */}
            <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-ocean/10">
                <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-3">
                        <BrandLogo variant="primary" className="w-28 md:w-32" />
                        <span className="hidden md:inline text-[10px] uppercase tracking-[0.3em] text-coral font-bold">
                            Admin
                        </span>
                    </Link>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={exportCsv}
                            className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 bg-ocean text-cream rounded-lg text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-ocean-800 transition-colors"
                            data-testid="admin-export-csv"
                        >
                            <Download size={14} /> Export CSV
                        </button>
                        <button
                            onClick={logout}
                            className="inline-flex items-center gap-2 px-4 py-2.5 border border-ocean/30 text-ocean rounded-lg text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-ocean hover:text-cream transition-colors"
                            data-testid="admin-logout"
                        >
                            <LogOut size={14} /> Déconnexion
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-[1400px] mx-auto px-5 md:px-8 py-10">
                {/* Title + stats */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.32em] text-coral font-bold mb-2">
                            Boîte de réception
                        </p>
                        <h1 className="font-serif text-4xl md:text-5xl text-ocean">
                            Demandes clients
                        </h1>
                    </div>
                    <div className="flex gap-4 text-sm">
                        <Stat
                            label="Total"
                            value={leads?.length ?? "—"}
                            testid="stat-total"
                        />
                        <Stat
                            label="Non lues"
                            value={unreadCount}
                            tone="coral"
                            testid="stat-unread"
                        />
                    </div>
                </div>

                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-3 mb-6">
                    <div className="relative flex-1">
                        <Search
                            size={16}
                            className="absolute left-4 top-1/2 -translate-y-1/2 text-ink/40"
                        />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Rechercher par nom, email, expérience…"
                            className="w-full pl-11 pr-4 py-3 bg-white border border-ocean/15 rounded-lg text-ocean focus:border-coral focus:outline-none transition-colors"
                            data-testid="admin-search"
                        />
                    </div>
                    <div className="flex gap-2">
                        {[
                            { k: "all", l: "Toutes" },
                            { k: "unread", l: "Non lues" },
                            { k: "read", l: "Lues" },
                        ].map((f) => (
                            <button
                                key={f.k}
                                onClick={() => setFilter(f.k)}
                                className={`px-4 py-2.5 rounded-lg text-[11px] uppercase tracking-[0.2em] font-bold transition-colors ${
                                    filter === f.k
                                        ? "bg-coral text-cream"
                                        : "bg-white border border-ocean/15 text-ink/70 hover:border-coral/50"
                                }`}
                                data-testid={`filter-${f.k}`}
                            >
                                {f.l}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Leads list */}
                {leads === null ? (
                    <div className="flex items-center justify-center py-20 text-ink/50">
                        <Loader2 className="animate-spin mr-3" /> Chargement…
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="text-center py-20 border border-dashed border-ocean/20 rounded-2xl bg-white/50">
                        <Inbox size={48} className="mx-auto mb-4 text-ocean/30" />
                        <p className="text-ink/60">
                            {leads.length === 0
                                ? "Aucune demande pour le moment. Dès qu'un visiteur enverra le formulaire, elle apparaîtra ici."
                                : "Aucune demande ne correspond à votre recherche."}
                        </p>
                    </div>
                ) : (
                    <ul className="space-y-3" data-testid="admin-leads-list">
                        {filtered.map((lead) => (
                            <li
                                key={lead.id}
                                className={`bg-white border rounded-2xl overflow-hidden transition-shadow ${
                                    lead.read
                                        ? "border-ocean/10"
                                        : "border-coral/30 shadow-[0_6px_20px_rgba(235,103,65,0.08)]"
                                }`}
                                data-testid={`lead-${lead.id}`}
                            >
                                <button
                                    onClick={() => toggleOpen(lead.id)}
                                    className="w-full text-left p-5 flex items-center gap-4 hover:bg-cream/40 transition-colors"
                                >
                                    {!lead.read && (
                                        <span
                                            className="w-2 h-2 bg-coral rounded-full flex-shrink-0"
                                            aria-label="Non lue"
                                        />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                                            <h3
                                                className={`font-serif text-xl ${
                                                    lead.read ? "text-ocean/80" : "text-ocean"
                                                }`}
                                            >
                                                {lead.name}
                                            </h3>
                                            {lead.experience && (
                                                <span className="text-[10px] uppercase tracking-[0.25em] text-ocean bg-lagoon/40 px-2 py-0.5 rounded font-bold">
                                                    {lead.experience}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-ink/60 mt-1 truncate">
                                            {lead.email}
                                            {lead.phone ? ` · ${lead.phone}` : ""}
                                        </p>
                                    </div>
                                    <time className="hidden md:block text-xs text-ink/50 whitespace-nowrap">
                                        {new Date(lead.created_at).toLocaleString(
                                            "fr-FR",
                                            {
                                                day: "2-digit",
                                                month: "short",
                                                year: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            },
                                        )}
                                    </time>
                                    <ChevronDown
                                        size={18}
                                        className={`text-ink/40 transition-transform ${
                                            opened.has(lead.id) ? "rotate-180" : ""
                                        }`}
                                    />
                                </button>

                                {opened.has(lead.id) && (
                                    <div className="px-5 pb-5 pt-1 border-t border-ocean/10 bg-cream/30">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 text-sm">
                                            <Field icon={Mail} label="Email">
                                                <a
                                                    href={`mailto:${lead.email}`}
                                                    className="text-coral hover:underline"
                                                >
                                                    {lead.email}
                                                </a>
                                            </Field>
                                            {lead.phone && (
                                                <Field icon={Phone} label="Téléphone">
                                                    <a
                                                        href={`tel:${lead.phone.replace(/\s/g, "")}`}
                                                        className="text-coral hover:underline"
                                                    >
                                                        {lead.phone}
                                                    </a>
                                                </Field>
                                            )}
                                            {lead.offer && (
                                                <Field label="Offre">
                                                    {lead.offer}
                                                </Field>
                                            )}
                                            {lead.addons?.length > 0 && (
                                                <Field label="Add-ons">
                                                    {lead.addons.join(", ")}
                                                </Field>
                                            )}
                                            <Field icon={Calendar} label="Reçue le">
                                                {new Date(lead.created_at).toLocaleString(
                                                    "fr-FR",
                                                )}
                                            </Field>
                                            <Field label="Source">
                                                {lead.source || "—"}
                                            </Field>
                                        </div>

                                        {lead.message && (
                                            <div className="mb-4">
                                                <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-ocean mb-2 flex items-center gap-2">
                                                    <MessageSquare size={12} /> Message
                                                </p>
                                                <div className="p-4 bg-white border border-ocean/10 rounded-lg text-ink whitespace-pre-wrap leading-relaxed">
                                                    {lead.message}
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex flex-wrap gap-2 pt-2">
                                            <button
                                                onClick={() => toggleRead(lead)}
                                                disabled={actionBusy === lead.id}
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-ocean text-cream rounded-lg text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-ocean-800 transition-colors disabled:opacity-60"
                                                data-testid={`toggle-read-${lead.id}`}
                                            >
                                                <Check size={13} />
                                                {lead.read ? "Marquer non lue" : "Marquer lue"}
                                            </button>
                                            <a
                                                href={`https://wa.me/${(lead.phone || "").replace(/\D/g, "")}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`inline-flex items-center gap-2 px-4 py-2 border border-coral text-coral rounded-lg text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-coral hover:text-cream transition-colors ${
                                                    !lead.phone ? "opacity-50 pointer-events-none" : ""
                                                }`}
                                            >
                                                Répondre WhatsApp
                                            </a>
                                            <button
                                                onClick={() => remove(lead)}
                                                disabled={actionBusy === lead.id}
                                                className="inline-flex items-center gap-2 px-4 py-2 border border-coral/40 text-coral/80 rounded-lg text-[11px] uppercase tracking-[0.2em] font-bold hover:bg-coral/10 transition-colors ml-auto disabled:opacity-60"
                                                data-testid={`delete-${lead.id}`}
                                            >
                                                <Trash2 size={13} /> Supprimer
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </main>
        </div>
    );
}

function Stat({ label, value, tone = "ocean", testid }) {
    return (
        <div
            className="px-5 py-3 bg-white border border-ocean/10 rounded-xl text-center min-w-[110px]"
            data-testid={testid}
        >
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-ink/50 mb-1">
                {label}
            </p>
            <p
                className={`font-serif text-2xl ${tone === "coral" ? "text-coral" : "text-ocean"}`}
            >
                {value}
            </p>
        </div>
    );
}

function Field({ icon: Icon, label, children }) {
    return (
        <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-ink/50 mb-1 flex items-center gap-2">
                {Icon && <Icon size={11} />}
                {label}
            </p>
            <p className="text-ink text-sm">{children}</p>
        </div>
    );
}
